import { useTranslation } from 'react-i18next';
import { DictLockerStatus } from 'shared';
import { BadgeBriefcaseIcon, BadgeUserIcon } from 'assets';
import { PillColor, RowItemPill } from './RowItemPill';

const STATUS_CONFIG: Record<DictLockerStatus, { labelKey: string; color: PillColor }> = {
	[DictLockerStatus.Available]: { labelKey: 'lockerStatus.available', color: 'apAvailable' },
	[DictLockerStatus.Rented]: { labelKey: 'lockerStatus.rented', color: 'apRented' },
	[DictLockerStatus.Reserved]: { labelKey: 'lockerStatus.reserved', color: 'amber' },
	[DictLockerStatus.Reservation]: { labelKey: 'lockerStatus.reservation', color: 'amber' },
	[DictLockerStatus.PendingVerification]: { labelKey: 'lockerStatus.pendingVerification', color: 'apPending' },
	// Resigned rentals are still occupied — show them like rented boxes (green badge + tenant name).
	[DictLockerStatus.Resigned]: { labelKey: 'lockerStatus.resigned', color: 'apRented' },
	[DictLockerStatus.OutOfUse]: { labelKey: 'lockerStatus.outOfUse', color: 'apOutOfUse' },
};

// Statuses that surface the tenant name in the badge area (so the head no longer repeats it).
export const TENANT_BADGE_STATUSES: DictLockerStatus[] = [
	DictLockerStatus.Rented,
	DictLockerStatus.PendingVerification,
	DictLockerStatus.Resigned,
];

interface StatusBadgeProps {
	lockerStatus?: DictLockerStatus;
	tenantName?: string;
	isCompany?: boolean;
	orderItemTypeTag?: string | null;
}

// Polish status adjectives agree with the unit's grammatical gender; offices are neuter, services feminine.
const GENDER_CONTEXT_BY_TYPE: Record<string, string> = {
	'#offices': 'offices',
	'#services': 'services',
};

export const StatusBadge = ({ lockerStatus, tenantName, isCompany, orderItemTypeTag }: StatusBadgeProps) => {
	const { t } = useTranslation();

	if (lockerStatus == null) return null;

	const config = STATUS_CONFIG[lockerStatus];
	if (!config) return null;

	// Statuses with a tenant: show the tenant name in the status colour, no separate status label.
	if (tenantName && TENANT_BADGE_STATUSES.includes(lockerStatus)) {
		return (
			<RowItemPill
				label={tenantName}
				color={config.color}
				icon={isCompany ? <BadgeBriefcaseIcon /> : <BadgeUserIcon />}
				fixedWidth
			/>
		);
	}

	const genderContext = orderItemTypeTag ? GENDER_CONTEXT_BY_TYPE[orderItemTypeTag] : undefined;
	const label = t(config.labelKey, genderContext ? { context: genderContext } : undefined);
	return <RowItemPill label={label} color={config.color} />;
};
